import { useState, useCallback, useMemo } from 'react';
import {
  RadialNav,
  SwotGrid,
  type SwotNodes,
  type SwotGridSection,
  type RadialNavBranch,
} from '@system-rec/decision-ui';

/* ------------------------------------------------------------------ */
/*  Branch definitions with nested children                            */
/* ------------------------------------------------------------------ */

const sampleBranches: RadialNavBranch[] = [
  {
    id: 'fabric',
    label: 'Fabric',
    icon: '🏠',
    weight: 4,
    children: [
      { id: 'insulation', label: 'Insulation', icon: '🧱', weight: 2, values: ['none', 'partial', 'full'] },
      { id: 'windows', label: 'Windows', icon: '🪟', weight: 2, values: ['single', 'double', 'triple'] },
      { id: 'draughtproofing', label: 'Draught-proofing', icon: '🌬️', weight: 1, values: ['none', 'partial', 'full'] },
    ],
  },
  {
    id: 'distribution',
    label: 'Distribution',
    icon: '🛤️',
    weight: 3,
    children: [
      { id: 'pipeSize', label: 'Pipe Size', icon: '🔧', weight: 2, values: ['22mm', '28mm', 'unknown'] },
      { id: 'planType', label: 'Plan Type', icon: '📋', weight: 2, values: ['y_plan', 's_plan', 'unknown'] },
      { id: 'filtration', label: 'Filtration', icon: '🧹', weight: 1, values: ['none', 'magnetic', 'laminar'] },
    ],
  },
  {
    id: 'hotwater',
    label: 'Hot Water',
    icon: '🚿',
    weight: 3,
    children: [
      { id: 'cylinder', label: 'Cylinder', icon: '🪣', weight: 2, values: ['none', 'vented', 'unvented'] },
      { id: 'flowRate', label: 'Flow Rate', icon: '💧', weight: 1, values: ['low', 'medium', 'high'] },
    ],
  },
  {
    id: 'heating',
    label: 'Heating',
    icon: '🔥',
    weight: 4,
    children: [
      { id: 'boilerType', label: 'Boiler Type', icon: '🏭', weight: 3, values: ['combi', 'system', 'regular', 'back_boiler'] },
      { id: 'boilerAge', label: 'Boiler Age', icon: '📅', weight: 2, values: ['<5', '5-10', '10-15', '15+'] },
      { id: 'emitters', label: 'Emitters', icon: '🌡️', weight: 2, values: ['radiators', 'underfloor', 'mixed'] },
    ],
  },
  {
    id: 'ventilation',
    label: 'Ventilation',
    icon: '💨',
    weight: 2,
    children: [
      { id: 'ventType', label: 'Vent Type', icon: '🌀', weight: 1, values: ['natural', 'extract', 'mvhr'] },
    ],
  },
  {
    id: 'controls',
    label: 'Controls',
    icon: '🎛️',
    weight: 3,
    children: [
      { id: 'thermostat', label: 'Thermostat', icon: '🌡️', weight: 2, values: ['none', 'basic', 'programmable', 'smart'] },
      { id: 'trvs', label: 'TRVs', icon: '🔘', weight: 1, values: ['none', 'some', 'all'] },
    ],
  },
  {
    id: 'renewable',
    label: 'Renewable',
    icon: '☀️',
    weight: 3,
    children: [
      { id: 'solarPV', label: 'Solar PV', icon: '⚡', weight: 2, values: ['none', 'installed', 'planned'] },
      { id: 'heatPump', label: 'Heat Pump', icon: '♨️', weight: 2, values: ['none', 'ashp', 'gshp'] },
    ],
  },
  {
    id: 'storage',
    label: 'Storage',
    icon: '🔋',
    weight: 2,
    children: [
      { id: 'battery', label: 'Battery', icon: '🔋', weight: 1, values: ['none', 'installed', 'planned'] },
      { id: 'thermal', label: 'Thermal Store', icon: '🫙', weight: 1, values: ['none', 'installed'] },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Helper: derive SWOT nodes from the live payload                    */
/* ------------------------------------------------------------------ */

function deriveSwotFromPayload(payload: Record<string, any>): {
  statusQuo: SwotGridSection;
  recommended: SwotGridSection;
} {
  const strengths: SwotNodes['strengths'] = [];
  const weaknesses: SwotNodes['weaknesses'] = [];
  const opportunities: SwotNodes['opportunities'] = [];
  const threats: SwotNodes['threats'] = [];

  // Fabric assessment
  if (payload.insulation === 'full') {
    strengths.push({ id: 's-insulation', label: 'Well Insulated', icon: '🧱' });
  } else {
    weaknesses.push({ id: 'w-insulation', label: payload.insulation === 'partial' ? 'Partial Insulation' : 'No Insulation', icon: '🧱' });
  }

  if (payload.windows === 'triple') {
    strengths.push({ id: 's-windows', label: 'Triple Glazing', icon: '🪟' });
  } else if (payload.windows === 'double') {
    strengths.push({ id: 's-windows', label: 'Double Glazing', icon: '🪟' });
  } else if (payload.windows === 'single') {
    weaknesses.push({ id: 'w-windows', label: 'Single Glazed', icon: '🪟' });
  }

  // Heating assessment
  const age = payload.boilerAge;
  if (age === '15+') {
    weaknesses.push({ id: 'w-boiler-age', label: 'Boiler 15+ years', icon: '📅', hasAnimation: true });
    threats.push({ id: 't-breakdown', label: 'Breakdown Risk', icon: '⚠️' });
  } else if (age === '10-15') {
    weaknesses.push({ id: 'w-boiler-age', label: 'Ageing Boiler', icon: '📅' });
  } else if (age === '<5') {
    strengths.push({ id: 's-boiler-age', label: 'Recent Boiler', icon: '📅' });
  }

  // Distribution
  if (payload.pipeSize === '28mm') {
    strengths.push({ id: 's-pipe', label: '28mm Primary', icon: '🔧' });
  } else if (payload.pipeSize === '22mm') {
    weaknesses.push({ id: 'w-pipe', label: '22mm Primary', icon: '🔧' });
  }

  if (payload.filtration === 'laminar') {
    strengths.push({ id: 's-filter', label: 'Laminar Filter', icon: '🧹' });
  } else if (payload.filtration === 'none') {
    weaknesses.push({ id: 'w-filter', label: 'No Filtration', icon: '🧹' });
  }

  if (payload.planType === 's_plan') {
    strengths.push({ id: 's-plan', label: 'S-Plan Zoning', icon: '📋' });
  }

  // Controls
  if (payload.thermostat === 'smart') {
    strengths.push({ id: 's-smart', label: 'Smart Thermostat', icon: '🌡️' });
  } else if (payload.thermostat === 'none') {
    weaknesses.push({ id: 'w-therm', label: 'No Thermostat', icon: '🌡️' });
  }

  // Renewables
  if (payload.heatPump && payload.heatPump !== 'none') {
    strengths.push({ id: 's-hp', label: 'Heat Pump', icon: '♨️' });
  } else {
    opportunities.push({ id: 'o-hp', label: 'Heat Pump Ready', icon: '♨️', hasAnimation: true });
  }

  if (payload.solarPV === 'installed') {
    strengths.push({ id: 's-solar', label: 'Solar PV', icon: '⚡' });
  } else {
    opportunities.push({ id: 'o-solar', label: 'Solar Potential', icon: '☀️' });
  }

  // Always present threats
  threats.push({ id: 't-gas', label: 'Rising Gas Prices', icon: '💰' });

  // Determine how many fields are filled
  const filled = Object.values(payload).filter(v => v !== undefined && v !== '').length;
  const hasData = filled > 0;

  // Build the recommended section: flip weaknesses → proposed fixes
  const recStrengths: SwotNodes['strengths'] = [];
  const recOpportunities: SwotNodes['opportunities'] = [];

  // Proposed system fixes weaknesses
  recStrengths.push({ id: 'rs-modern', label: 'Modern System', icon: '✅' });
  if (weaknesses.some((w: { id: string }) => w.id === 'w-filter')) {
    recStrengths.push({ id: 'rs-filter', label: 'Laminar Filtration', icon: '🧹' });
  }
  if (weaknesses.some((w: { id: string }) => w.id === 'w-pipe')) {
    recStrengths.push({ id: 'rs-pipe', label: 'Optimised Pipework', icon: '🔧' });
  }
  if (weaknesses.some((w: { id: string }) => w.id === 'w-therm')) {
    recStrengths.push({ id: 'rs-therm', label: 'Smart Controls', icon: '🎛️' });
  }
  recStrengths.push({ id: 'rs-eff', label: 'Higher Efficiency', icon: '📈' });

  recOpportunities.push({ id: 'ro-carbon', label: 'Lower Carbon', icon: '🌱' });
  recOpportunities.push({ id: 'ro-cost', label: 'Lower Running Cost', icon: '💷' });

  return {
    statusQuo: {
      label: hasData ? 'Your Current System' : 'Status Quo (fill data to populate)',
      nodes: {
        strengths,
        weaknesses,
        opportunities: [],
        threats,
      },
    },
    recommended: {
      label: 'Recommended Option',
      nodes: {
        strengths: recStrengths,
        weaknesses: [],
        opportunities: recOpportunities,
        threats: [],
      },
    },
  };
}

/* ------------------------------------------------------------------ */
/*  Demo Page                                                          */
/* ------------------------------------------------------------------ */

export default function RadialSwotDemoPage() {
  // Central payload — the single source of truth
  const [payload, setPayload] = useState<Record<string, any>>({});
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);

  const handleBranchSelect = useCallback((branchId: string) => {
    setSelectedBranch(branchId);
  }, []);

  // This is the critical onChange wire: every tap updates the payload immediately
  const handleChange = useCallback((_branchId: string, key: string, value: string) => {
    setPayload(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleNodeClick = useCallback((nodeId: string, type: string) => {
    console.log('SWOT node:', nodeId, type);
  }, []);

  // Derive SWOT data from the live payload
  const swotData = useMemo(() => deriveSwotFromPayload(payload), [payload]);

  return (
    <div style={{ width: '100%', minHeight: '100vh', background: '#f9fafb' }}>
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
          Radial Mind Map &amp; SWOT Stack
        </h1>
        <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
          Tap a branch to expand child nodes. Select values to update the JSON payload and SWOT in real-time.
        </p>
      </div>

      {/* Presentation Mode Layout */}
      <div className="presentation-mode">
        <div className="presentation-mode__input">
          <div>
            <h2 style={{ textAlign: 'center', marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '600' }}>
              Data Entry: Recursive Radial Map
            </h2>
            <RadialNav
              data={payload}
              onSelect={handleBranchSelect}
              onChange={handleChange}
              hubLabel="THE HOME"
              branches={sampleBranches}
            />
            {selectedBranch && (
              <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                <p style={{ color: '#1f2937', fontWeight: '500', fontSize: '0.875rem' }}>
                  Active: <strong>{selectedBranch}</strong>
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="presentation-mode__output" style={{ flexDirection: 'column', padding: '1rem' }}>
          <h2 style={{ textAlign: 'center', padding: '0.5rem', fontSize: '1.25rem', fontWeight: '600' }}>
            Portrait SWOT Stack
          </h2>
          <SwotGrid
            statusQuo={swotData.statusQuo}
            recommended={swotData.recommended}
            onNodeClick={handleNodeClick}
          />
        </div>
      </div>

      {/* Live JSON payload inspector */}
      <div style={{ maxWidth: '1200px', margin: '2rem auto', padding: '0 2rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem' }}>
          Live Payload (SystemRecommendation wire)
        </h2>
        <pre
          style={{
            background: '#1e293b',
            color: '#e2e8f0',
            padding: '1rem',
            borderRadius: '8px',
            fontSize: '0.8125rem',
            overflow: 'auto',
            maxHeight: '300px',
          }}
        >
          {JSON.stringify(payload, null, 2) || '{}'}
        </pre>
      </div>

      {/* Feature notes */}
      <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
          What Changed
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
          <div>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              Recursive Branching
            </h3>
            <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', color: '#4b5563', lineHeight: '1.8' }}>
              <li>Tapping a branch spawns physical child nodes at an increased radius</li>
              <li>Children animate in with spring physics via framer-motion AnimatePresence</li>
              <li>Each node can have its own children (recursive to any depth)</li>
              <li>Leaf nodes display selectable value pills</li>
            </ul>
          </div>
          <div>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              JSON Sync &amp; SWOT Stack
            </h3>
            <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', color: '#4b5563', lineHeight: '1.8' }}>
              <li>Every tap calls onChange(branchId, key, value) updating the central payload</li>
              <li>SWOT grids derive from the live payload in real-time (useMemo)</li>
              <li>Portrait SWOT Stack: Status Quo (top) vs Recommended (bottom)</li>
              <li>Quadrants resize dynamically based on node count (flex-grow)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
